import React from "react";
import { Box, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LockClockIcon from "@mui/icons-material/LockClock";
import PublicIcon from "@mui/icons-material/Public";
import SchoolIcon from "@mui/icons-material/School";
import {
  accent, navy, success, warning, textPrimary, textMuted, font, border, surfaceRaised,
} from "../layouts/schoolPortal/schoolPortalTheme";

// Small, hand-built diagrams in the same spirit as the consumer app's AI Learn
// onboarding popup (divs + inline icons instead of imported artwork) — themed
// to the school-portal palette rather than reusing the consumer app's tokens.

const badge = (color, size = 56) => ({
  width: size, height: size, borderRadius: "16px",
  display: "flex", alignItems: "center", justifyContent: "center",
  backgroundColor: `${color}14`, border: `1.5px solid ${color}33`,
  boxShadow: `0 4px 14px ${color}22`,
});

const arrow = (color) => (
  <Box sx={{ display: "flex", alignItems: "center", px: 0.5 }}>
    <Box sx={{ width: 22, height: 2, backgroundColor: color, opacity: 0.5 }} />
    <Box sx={{
      width: 0, height: 0, borderTop: "4px solid transparent", borderBottom: "4px solid transparent",
      borderLeft: `6px solid ${color}`, opacity: 0.5,
    }} />
  </Box>
);

// Student pays -> money held -> instructor/school paid out.
export function PaymentFlowIllustration() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
        <Box sx={badge(navy)}><PersonIcon sx={{ color: navy, fontSize: 26 }} /></Box>
        <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: textMuted, fontFamily: font }}>Student</Typography>
      </Box>
      {arrow(navy)}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
        <Box sx={badge(accent)}><CreditCardIcon sx={{ color: accent, fontSize: 24 }} /></Box>
        <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: textMuted, fontFamily: font }}>Held</Typography>
      </Box>
      {arrow(success)}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
        <Box sx={badge(success)}><AccountBalanceIcon sx={{ color: success, fontSize: 24 }} /></Box>
        <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: textMuted, fontFamily: font }}>You</Typography>
      </Box>
    </Box>
  );
}

// A mini week-view calendar grid — available by default within working
// hours, with a couple of closed/reserved exceptions carved out.
export function CalendarGridIllustration() {
  const cellState = [
    ["avail", "avail", "off", "avail", "off", "off", "off"],
    ["off", "closed", "avail", "avail", "off", "reserved", "off"],
    ["avail", "off", "avail", "off", "avail", "off", "off"],
  ];
  const colorFor = (s) => (s === "closed" ? navy : s === "reserved" ? warning : "transparent");
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Box sx={{
        display: "grid", gridTemplateColumns: "repeat(7, 16px)", gridAutoRows: "16px", gap: "4px",
        p: 1.5, backgroundColor: surfaceRaised, borderRadius: "12px", border: `1px solid ${border}`,
      }}>
        {cellState.flat().map((s, i) => (
          <Box key={i} sx={{
            width: 16, height: 16, borderRadius: "4px",
            backgroundColor: s === "off" ? "rgba(26,26,26,0.06)" : s === "avail" ? `${accent}22` : colorFor(s),
            border: s === "avail" ? `1px solid ${accent}55` : "none",
            opacity: s === "closed" || s === "reserved" ? 0.85 : 1,
          }} />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        {[["Available", accent], ["Closed", navy], ["Reserved", warning]].map(([label, color]) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "3px", backgroundColor: color }} />
            <Typography sx={{ fontSize: "9.5px", color: textMuted, fontFamily: font, fontWeight: 600 }}>{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// A pending hold branching into accept (captured) or decline (released).
export function AcceptDeclineIllustration() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
        <Box sx={badge(textMuted)}><LockClockIcon sx={{ color: textMuted, fontSize: 24 }} /></Box>
        <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: textMuted, fontFamily: font }}>Request</Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, ml: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {arrow(success)}
          <Box sx={badge(success, 40)}><CheckIcon sx={{ color: success, fontSize: 18 }} /></Box>
          <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: success, fontFamily: font }}>Accept → paid</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {arrow(accent)}
          <Box sx={badge(accent, 40)}><CloseIcon sx={{ color: accent, fontSize: 18 }} /></Box>
          <Typography sx={{ fontSize: "10.5px", fontWeight: 700, color: accent, fontFamily: font }}>Decline → released</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// In-house (own school only) vs Marketplace (cross-school reach) — XAir's 15% fee applies to both.
export function ChannelSplitIllustration() {
  const panel = (icon, label, tag, tagColor) => (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
      p: 2, borderRadius: "14px", backgroundColor: surfaceRaised, border: `1px solid ${border}`, width: 108,
    }}>
      <Box sx={badge(tagColor, 44)}>{icon}</Box>
      <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: textPrimary, fontFamily: font, textAlign: "center" }}>{label}</Typography>
      <Box sx={{ px: 1, py: 0.25, borderRadius: "999px", backgroundColor: `${tagColor}18` }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 800, color: tagColor, fontFamily: font }}>{tag}</Typography>
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {panel(<SchoolIcon sx={{ color: navy, fontSize: 22 }} />, "In-house", "Own students", navy)}
      {panel(<PublicIcon sx={{ color: accent, fontSize: 22 }} />, "Marketplace", "Worldwide", accent)}
    </Box>
  );
}

// A mini ranked leaderboard — "who's doing best" at a glance.
export function RollupIllustration() {
  const rows = [
    { w: "88%", color: accent },
    { w: "62%", color: navy },
    { w: "40%", color: success },
  ];
  return (
    <Box sx={{ width: 200, display: "flex", flexDirection: "column", gap: 1 }}>
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 20, height: 20, borderRadius: "999px", flexShrink: 0,
            backgroundColor: `${r.color}18`, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography sx={{ fontSize: "9.5px", fontWeight: 800, color: r.color, fontFamily: font }}>{i + 1}</Typography>
          </Box>
          <Box sx={{ flex: 1, height: 8, borderRadius: 999, backgroundColor: "rgba(26,26,26,0.06)", overflow: "hidden" }}>
            <Box sx={{ width: r.w, height: "100%", borderRadius: 999, backgroundColor: r.color }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}
